import { useCallback, useEffect, useRef, useState } from 'react';

export const usePromisedReady = ({ isReadyToCapture = true }: { isReadyToCapture?: boolean } = {}) => {
    const [isPromisesReady, setPromisesReady] = useState(false);
    const [isReadyToWatchPromises, setReadyToWatchPromises] = useState(false);
    const [loadingPromises, setLoadingPromises] = useState<Promise<void>[]>([]);
    const promiseRef = useRef<PromiseWithResolvers<void>>(Promise.withResolvers());

    useEffect(() => {
        if (!isReadyToWatchPromises) {
            return;
        }

        const abortController = new AbortController();

        (async () => {
            try {
                await Promise.all(loadingPromises);
            } finally {
                if (!abortController.signal.aborted) {
                    promiseRef.current.resolve();
                    setPromisesReady(true);

                    // ensure that all stored promises are released
                    // to prevent memory leaks
                    loadingPromises.length = 0;
                }
            }
        })();

        return () => abortController.abort();
    }, [isReadyToWatchPromises, loadingPromises]);

    /**
     * Needs to happen in a separate effect in order to allow 1 cycle to go through
     * all components that are being rendered after isReadyToCapture is true.
     */
    useEffect(() => {
        if (isReadyToCapture) {
            setReadyToWatchPromises(true);
        }
    }, [isReadyToCapture]);

    /**
     * In case the element is removed from the page completely, automatically resolve the promise.
     */
    useEffect(() => {
        const { resolve } = promiseRef.current;

        return () => resolve();
    }, [promiseRef]);

    const addLoadingPromise = useCallback((promise: Promise<void>) => {
        setLoadingPromises((prev) => [...prev, promise]);
    }, []);

    return [isPromisesReady, addLoadingPromise, promiseRef.current.promise];
};
